// doc/09 Phase 2: "π computed" provenance. When a card's numbers came out
// of a python_compute sandbox run, write_card's `computed_from` makes the
// executor stamp a server-validated `extra.computed` block — the agent
// cannot forge it. This module renders the badge (CardRow top row +
// CardPeek header) and the detail panel (CardPeek body).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Card } from '../../api'

/** Shape of `card.extra.computed` — mirrors the blob the executor's
 *  `resolve_card_extra` writes from a validated `sandbox_executions` row. */
export interface CardComputed {
  execution_id: number
  exit_code: number | null
  duration_ms: number | null
  code: string
  stdout: string | null
  input_card_refs: string[]
}

/** Safely pull a typed `CardComputed` off a card's untyped `extra` jsonb.
 *  Returns null unless `extra.computed` is an object carrying a numeric
 *  `execution_id` — the one field the executor always sets. */
export function getCardComputed(card: Card): CardComputed | null {
  const raw = card.extra?.['computed']
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (typeof o.execution_id !== 'number') return null
  return {
    execution_id: o.execution_id,
    exit_code: typeof o.exit_code === 'number' ? o.exit_code : null,
    duration_ms: typeof o.duration_ms === 'number' ? o.duration_ms : null,
    code: typeof o.code === 'string' ? o.code : '',
    stdout: typeof o.stdout === 'string' ? o.stdout : null,
    input_card_refs: Array.isArray(o.input_card_refs)
      ? o.input_card_refs.filter((x): x is string => typeof x === 'string')
      : [],
  }
}

/** Small `π` marker for a card's top row / peek header. Passive indicator
 *  with a tooltip — the click target is the surrounding card. */
export function ComputedBadge() {
  const { t } = useTranslation()
  return (
    <span
      className="mono"
      title={t('computed.badgeTooltip')}
      style={{
        fontSize: 9.5,
        color: '#5a4b8a',
        fontWeight: 700,
        letterSpacing: '.04em',
        padding: '0 4px',
        borderRadius: 3,
        background: '#efecf8',
        border: '1px solid #c9bfe6',
        lineHeight: 1.5,
      }}
    >
      {t('computed.label')}
    </span>
  )
}

/** Full provenance panel for CardPeek's view body: collapsed by default to
 *  a one-line summary; expands to the sandbox code + stdout. Always shows
 *  the cards the computation drew from when any were cited. */
export function ComputedDetail({ computed }: { computed: CardComputed }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const meta: string[] = [t('computed.runNumber', { id: computed.execution_id })]
  if (computed.duration_ms != null) {
    meta.push(t('computed.durationMs', { ms: computed.duration_ms }))
  }
  if (computed.exit_code != null && computed.exit_code !== 0) {
    meta.push(t('computed.exitCode', { code: computed.exit_code }))
  }
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed var(--rule)' }}>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className="mono"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: 10.5,
          color: '#5a4b8a',
          fontWeight: 700,
          letterSpacing: '.04em',
        }}
      >
        <span>{t('computed.label')}</span>
        <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{meta.join(' · ')}</span>
        <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{open ? '▾' : '▸'}</span>
      </button>
      {computed.input_card_refs.length > 0 && (
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 5 }}>
          {t('computed.inputs')} {computed.input_card_refs.join(', ')}
        </div>
      )}
      {open && (
        <>
          <pre style={computedPre}>{computed.code}</pre>
          {computed.stdout && (
            <>
              <div
                className="mono"
                style={{
                  fontSize: 9.5,
                  color: 'var(--ink-3)',
                  letterSpacing: '.08em',
                  fontWeight: 700,
                  margin: '8px 0 2px',
                }}
              >
                {t('computed.stdout')}
              </div>
              <pre style={computedPre}>{computed.stdout}</pre>
            </>
          )}
        </>
      )}
    </div>
  )
}

const computedPre: React.CSSProperties = {
  margin: '6px 0 0',
  padding: 8,
  background: 'var(--paper-1)',
  border: '1px solid var(--rule)',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: 'JetBrains Mono, monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 260,
  overflow: 'auto',
}
